import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Paths } from "@/paths";
import {
    Button, Card, EmptyState, ErrorNote, LaunchStatusBadge, PageHeader, Pager, ReadOnlyTag, Skeleton, Tag, ds, type GridColumn,
} from "@/shared/components/ds";
import { OpLaunchPage } from "../OpLaunchPage";
import { show } from "../format";
import type { LaunchListFilterOptions, LaunchListQuery, LaunchListResponse, LaunchListRow } from "../models/launchList";
import { CommandGetLaunchList } from "../services/cmdGetLaunchList";
import { CommandGetLaunchListFilters } from "../services/cmdGetLaunchListFilters";
import { LaunchListFilters, NO_FILTERS } from "./LaunchListFilters";
import { queryFromSearch, searchFromQuery } from "./launchListQuery";
import styles from "./launchList.module.css";

/** The 12 columns of OP's /launches/ page, in its order, plus the row chevron. `sortKey` where the server sorts. */
const COLUMNS: GridColumn[] = [
    { label: "ID", track: "72px", sortKey: "id" },
    { label: "Name", track: "minmax(280px, 1.6fr)", sortKey: "name" },
    { label: "Affiliate", track: "170px", sortKey: "affiliate" },
    { label: "Start Date", track: "100px", sortKey: "start_date" },
    { label: "End Date", track: "100px", sortKey: "end_date" },
    { label: "Status at Shutdown", track: "160px", sortKey: "status" },
    { label: "In Def.", track: "74px", sortKey: "in_definition" },
    { label: "Control Group", track: "118px" },
    { label: "Launch Attributes", track: "340px" },
    { label: "Engagement Team", track: "210px" },
    { label: "Created By", track: "130px" },
    { label: "Last Modified", track: "150px", sortKey: "datetime_modified" },
    { label: "", track: "32px" },
];
const TRACKS = COLUMNS.map((c) => c.track).join(" ");
const SHOWN_ATTRS = 3;

function YesNo({ value }: { value?: boolean | null }) {
    if (value === null || value === undefined) return <span className={ds.muted}>—</span>;
    return <span className={value ? undefined : styles.no}>{value ? "Yes" : "No"}</span>;
}

function Person({ name }: { name?: string | null }) {
    return name ? <span>{name}</span> : <span className={styles.unassigned}>Unassigned</span>;
}

/** The ID column heads; the first two stay pinned while the rest scroll. */
function Head({ sort, onSort }: { sort: string; onSort: (sort: string) => void }) {
    return (
        <div className={`${ds.grid} ${ds.gridHead} ${styles.head}`} style={{ gridTemplateColumns: TRACKS }} role="row">
            {COLUMNS.map((c, i) => {
                const key = c.sortKey;
                const state = key && sort === key ? "ascending" : key && sort === `-${key}` ? "descending" : undefined;
                const sticky = i === 0 ? `${styles.stickyId} ${styles.headSticky}` : i === 1 ? `${styles.stickyName} ${styles.headSticky}` : "";
                return (
                    <div key={i} role="columnheader" aria-sort={state}
                        className={`${ds.th} ${key ? ds.thSort : ""} ${state ? ds.thOn : ""} ${sticky}`}
                        onClick={key ? () => onSort(sort === key ? `-${key}` : key) : undefined}>
                        <span>{c.label}</span>
                        {state && <span className={ds.thArrow} aria-hidden="true">{state === "ascending" ? "↑" : "↓"}</span>}
                    </div>
                );
            })}
        </div>
    );
}

function Row({ row, onOpen }: { row: LaunchListRow; onOpen: () => void }) {
    const [expanded, setExpanded] = useState(false);
    const attrs = row.launch_attributes ?? [];
    const extra = attrs.length - SHOWN_ATTRS;
    const cell = (content: ReactNode, extraClass = "", nowrap = false) => (
        <div className={`${ds.td} ${extraClass} ${nowrap ? ds.nowrap : ""}`} role="cell">{content}</div>
    );
    return (
        <div className={`${ds.grid} ${ds.gridRow} ${ds.gridRowClick}`} style={{ gridTemplateColumns: TRACKS, alignItems: "center" }} role="row" onClick={onOpen}>
            {cell(
                <Link to={`/${Paths.OP_LAUNCHES}/${row.id}`} className={styles.idLink} title="View Launch Details" onClick={(e) => e.stopPropagation()}>{row.id}</Link>,
                styles.stickyId,
            )}
            {cell(<span className={`${styles.ellipsis} ${ds.toneStrong}`} title={row.name ?? undefined}>{show(row.name)}</span>, `${styles.stickyName} ${ds.toneStrong}`)}
            {cell(<span className={styles.ellipsis} title={row.affiliate_name ?? undefined}>{show(row.affiliate_name)}</span>)}
            {cell(show(row.start_date), ds.toneMuted, true)}
            {cell(show(row.end_date), ds.toneMuted, true)}
            {cell(<LaunchStatusBadge active={row.is_active} />)}
            {cell(<YesNo value={row.in_definition} />)}
            {cell(<YesNo value={row.has_control_group} />)}
            <div className={styles.attrs} role="cell" title={attrs.join(", ")}>
                {(expanded ? attrs : attrs.slice(0, SHOWN_ATTRS)).map((a) => <Tag key={a} mono>{a}</Tag>)}
                {attrs.length === 0 && <span className={ds.muted}>—</span>}
                {extra > 0 && (
                    <button type="button" className={styles.more} onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
                        {expanded ? "Less" : `+${extra} more`}
                    </button>
                )}
            </div>
            <div className={styles.team} role="cell">
                <div className={styles.teamRow}><span className={styles.teamKey}>EM</span><Person name={row.engagement_manager} /></div>
                <div className={styles.teamRow}><span className={styles.teamKey}>OM</span><Person name={row.ops_manager} /></div>
                <div className={styles.teamRow}><span className={styles.teamKey}>SL</span><Person name={row.sales_lead} /></div>
            </div>
            {cell(show(row.created_by), "", true)}
            {cell(show(row.datetime_modified), ds.toneMuted, true)}
            <div className={styles.chevron} role="cell" aria-hidden="true">›</div>
        </div>
    );
}

const SKELETON_WIDTHS = [186, 144, 210, 165, 198, 120, 174, 216];

function SkeletonRow({ width }: { width: number }) {
    return (
        <div className={`${ds.grid} ${ds.gridRow} ${styles.skeletonRow}`} style={{ gridTemplateColumns: TRACKS }} aria-hidden="true">
            {[42, width, 110, 70, 70, 72, 26, 26, 180, 130, 80, 100].map((w, i) => (
                <div key={i} style={{ padding: "0 14px" }}><Skeleton width={w} height={i === 5 ? 18 : 10} dark={i === 1} /></div>
            ))}
            <div />
        </div>
    );
}

export default function OpLaunchListPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const query = queryFromSearch(searchParams);
    const [options, setOptions] = useState<LaunchListFilterOptions | null>(null);
    const [page, setPage] = useState<LaunchListResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const update = (patch: Partial<LaunchListQuery>) =>
        setSearchParams(searchFromQuery({ ...query, page: 1, ...patch }));
    const goToPage = (n: number) => setSearchParams(searchFromQuery({ ...query, page: n }));

    useEffect(() => {
        new CommandGetLaunchListFilters().run().then(setOptions).catch(() => setOptions({}));
    }, []);

    useEffect(() => {
        let live = true;
        setError(null);
        setLoading(true);
        new CommandGetLaunchList().run(query)
            .then((result) => { if (live) setPage(result); })
            .catch((e) => { if (live) setError(e instanceof Error ? e.message : "Could not load the launches."); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [searchParams]);

    const count = page?.count ?? 0;
    const first = count ? (query.page - 1) * query.page_size + 1 : 0;
    const last = Math.min(query.page * query.page_size, count);
    const pages = Math.max(1, Math.ceil(count / query.page_size));
    const rows = page?.results ?? [];
    const showEmpty = !loading && !error && page !== null && rows.length === 0;

    return (
        <OpLaunchPage title="OP Launches">
            <PageHeader title="OP Launches"><ReadOnlyTag /></PageHeader>
            {error && <ErrorNote>{error}</ErrorNote>}
            <Card title="Launches" count={page?.count} clip={false}>
                <LaunchListFilters query={query} options={options} count={page?.count ?? null} onChange={update} />
                {showEmpty ? (
                    <div className={styles.emptyWrap}>
                        <EmptyState title="No launches match these filters"
                            body="Try a different affiliate, status at shutdown or search, or clear the filters."
                            action={<Button variant="secondary" size="sm" onClick={() => update(NO_FILTERS)}>Clear filters</Button>} />
                    </div>
                ) : (
                    <div className={styles.scroll} role="table" aria-busy={loading}>
                        <div className={styles.inner}>
                            <Head sort={query.sort} onSort={(sort) => update({ sort })} />
                            {loading
                                ? SKELETON_WIDTHS.map((w, i) => <SkeletonRow key={i} width={w} />)
                                : rows.map((row) => (
                                    <Row key={row.id} row={row} onOpen={() => navigate(`/${Paths.OP_LAUNCH_DASHBOARD}?launch_id=${row.id}`)} />
                                ))}
                        </div>
                    </div>
                )}
                <Pager first={first} last={last} total={count} noun="launches" loading={loading && page === null}
                    page={query.page} pages={pages} onPrev={() => goToPage(query.page - 1)} onNext={() => goToPage(query.page + 1)}
                    perPage={query.page_size} perOptions={[10, 25, 50, 100]} onPerPage={(page_size) => update({ page_size })} />
            </Card>
        </OpLaunchPage>
    );
}
